import { Pagination } from '@nile/components';
import {
  DateTimeCell,
  DependantCell,
  DualCell,
  HTMLCell,
  NameCell,
  PriorityCell,
  StepCell,
  TagCell,
  TextCell,
  TimerCell
} from '@nile/mangifera/src/Components/Ticket';
import {
  LinkCell,
  StageStatusCell,
  ImageCell
} from '@nile/client_management/src/Components/Cells';
import { TicketContext } from '@nile/mangifera/src/Contexts';
import { SortDown, SortUp } from '@styled-icons/boxicons-regular';
import PropTypes from 'prop-types';
import React, { useCallback, useContext, useEffect } from 'react';
import {
  useFlexLayout,
  useGlobalFilter,
  usePagination,
  useResizeColumns,
  useRowSelect,
  useSortBy,
  useTable
} from 'react-table';
import styled from 'styled-components';

const Styles = styled.div`
  .table {
    min-width: 400px !important;
    background: #ffffff;
  }

  .columHeader {
    box-shadow: none;
    height: 34px;
  }

  .tableHeader {
    border-top: 1px solid #ececec;
    border-bottom: 1px solid #ececec;
    padding-left: 16px;
  }
  .tr {
    background: #ebebfa;
  }

  .tbody {
    display: inline-block;
    max-height: 64vh;
    overflow: auto;
    padding-top: 1px;
    padding-left: 16px;
    .tr {
      height: 34px;
    }
    .td {
      flex: none !important;
    }
  }

  .inblock {
    height: 100%;
    flex: none !important;
  }

  .unfreezeHeader {
    height: 100%;
  }
  .colHeader {
    height: 100%;
    align-items: center;
  }

  .cell {
    padding-left: 0;
  }

  .unFreezeContent {
    width: 100%;
    overflow: auto;
    white-space: nowrap;
  }

  overflow: auto;
  /* Hide scrollbar for Chrome, Safari and Opera */
  .unfreezeHeader::-webkit-scrollbar {
    display: none;
  }

  .unfreezeHeader {
    scrollbar-width: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  .unfreezeHeader {
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  }

  .columnHeader {
    width: 100%;
  }

  .colHeader {
    text-transform: uppercase;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
    background: #ebebfa;
  }

  .tbody {
    .tr:hover {
      .td {
        border-bottom: 1px solid #ececec;
      }
      .editable-cell {
        background: #e3edff;
      }
      .edit-actions {
        visibility: visible;
      }
    }
    .editable-cell {
      border-radius: 4px;
      height: 80%;
    }
    .td {
      background: #ffffff;
      align-items: center !important;
      border-bottom: 1px solid transparent;
    }
  }

  .resizer {
    display: inline-block;
    //background: #ececec;
    width: 5px;
    height: 100%;
    position: absolute;
    right: 0;
    top: 0;
    transform: translateX(50%);
    z-index: 1;
    ${'' /* prevents from scrolling while dragging on touch devices */}
    touch-action:none;

    &.isResizing {
      background: red;
    }
  }
`;

const SortUpIcon = styled(SortUp)`
  width: 24px;
  height: 24px;
`;

const SortDownIcon = styled(SortDown)`
  width: 24px;
  height: 24px;
`;

const defaultColumn = {
  // minWidth: 30,
  // maxWidth: 500,
  width: 200,
  TEXT: TextCell,
  INVOICE_STATUS: TextCell,
  SHEET: TextCell
};

const cellProps = (props, { cell }) => getStyles(props, cell.column.align);

const getStyles = (props, align = 'left') => [
  props,
  {
    style: {
      justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
      alignItems: 'flex-start',
      display: 'flex'
    }
  }
];

function TableHeaders(props) {
  const { column } = props;
  return (
    <div
      className="inblock"
      {...(column.id === 'selection'
        ? column.getHeaderProps()
        : column.getHeaderProps(column.getSortByToggleProps()))}
    >
      <div
        className={`fx ${column.id !== 'selection' ? 'th colHeader' : 'fx'}`}
      >
        <div>{column.render('Header')}</div>
        {column.canResize && (
          <div
            {...column.getResizerProps()}
            className={`resizer ${column.isResizing ? 'isResizing' : ''}`}
          />
        )}
        {column.id !== 'selection' ? (
          <div className="icon" active={column.isSorted}>
            {column.isSortedDesc ? <SortDownIcon /> : <SortUpIcon />}
          </div>
        ) : null}
      </div>
    </div>
  );
}

const InvoiceItemsTable = ({
  columns,
  data,
  pagination,
  updateMyData,
  skipPageReset,
  refreshTable,
  rowCount,
  pages,
  activePage
}) => {
  const {
    getTableProps,
    headerGroups,
    prepareRow,
    page,
    setPageSize,
    state: { pageIndex, pageSize, selectedRowIds }
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      autoResetPage: !skipPageReset,
      updateMyData,
      refreshTable
    },
    useResizeColumns,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useFlexLayout
  );

  const freezedColumns = React.createRef();
  const normalcolumns = React.createRef();
  const normalColHeaders = React.createRef();

  const scollTableBody = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (
      !(
        e.target === freezedColumns.current ||
        e.target === normalColHeaders.current ||
        e.target === normalcolumns?.current
      )
    ) {
      return;
    }
    const scrollElement =
      e.target === freezedColumns.current ? normalcolumns : freezedColumns;
    if (scrollElement.current)
      scrollElement.current.scrollTop = e.target.scrollTop;
    if (normalColHeaders.current)
      normalColHeaders.current.scrollLeft = e.target.scrollLeft;
    if (e.target === normalColHeaders.current)
      normalcolumns.current.scrollLeft = e.target.scrollLeft;
  };

  const context = useContext(TicketContext);

  useEffect(() => {
    setPageSize(rowCount);
  }, [rowCount]);

  useEffect(() => {
    setPageSize(rowCount);
  }, []);

  const navigatePages = useCallback((pageNumber) => {
    console.log('calling', pageNumber);
    refreshTable(context.personalization, {
      pageSize: rowCount,
      page: pageNumber || 0
    });
  });
  // Render the UI for your table
  return (
    <Styles>
      <div {...getTableProps()} className="table">
        <div>
          {headerGroups.map((headerGroup) => (
            <div
              {...headerGroup.getHeaderGroupProps()}
              className="tr tableHeader"
            >
              <div className="fx columHeader">
                <div
                  className="unFreezeContent fx fh actr unfreezeHeader"
                  onScroll={scollTableBody}
                  ref={normalColHeaders}
                >
                  {headerGroup.headers.map((column, i) => {
                    return <TableHeaders column={column}></TableHeaders>;
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div>
          <div
            className="tbody unFreezeContent"
            onScroll={scollTableBody}
            ref={normalcolumns}
          >
            {page.map((row, i) => {
              prepareRow(row);
              return (
                <div {...row.getRowProps()} className="tr">
                  {row.cells.map((cell, j) => {
                    return (
                      <div {...cell.getCellProps(cellProps)} className="td">
                        {cell.column.hasOwnProperty('type')
                          ? cell.render(cell.column.type)
                          : cell.render('Cell')}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {pagination && (
        <Pagination
          onPageChange={navigatePages}
          setPageSize={setPageSize}
          pages={pages}
          page={activePage}
        />
      )}
    </Styles>
  );
};

export default InvoiceItemsTable;
